import { useOidcContext } from '../contexts/OidcContext';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { api } from '../api';
import { config } from '../config';
import { OrgInfoOut as OrgInfo } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';
import { OrgInfoContext } from '../contexts/OrgInfoContext';
import RegistrationPage from '../pages/RegistrationPage';
import PendingPage from '../pages/PendingPage';
import MemberPendingPage from '../pages/MemberPendingPage';
import SignupProgress from './SignupProgress';
import AccessDeniedCard from './AccessDeniedCard';
import FatalErrorCard from './FatalErrorCard';
import JoinPromptCard from './JoinPromptCard';
import OrgSignupPromptCard from './OrgSignupPromptCard';
import { decodeJwtPayload } from '../utils/decodeJwt';
import {
  readSignupIntent,
  clearSignupIntent,
  type SignupIntent,
} from '../utils/signupIntent';

const DIRECT_BROKER_INTENT_KEY = 'portal_direct_broker_intent';
const RETURN_TO_KEY = 'portal_return_to';

function isSafeReturnTo(value: string | null): value is string {
  // Only allow same-origin paths; block protocol-relative URLs and anything that
  // doesn't look like a single in-app route.
  return !!value && value.startsWith('/') && !value.startsWith('//');
}

type IdpAlias = 'google' | 'microsoft-entra';

function readDirectBrokerIntent(): IdpAlias | null {
  const raw = sessionStorage.getItem(DIRECT_BROKER_INTENT_KEY);
  return raw === 'google' || raw === 'microsoft-entra' ? raw : null;
}

interface JoinableOrg {
  idp_org_id: string;
  organization_name: string;
}

function isBeheerderToken(token: string): boolean {
  const payload = decodeJwtPayload<{ realm_access?: { roles?: string[] } }>(token);
  const realmRoles = payload?.realm_access?.roles ?? [];
  return realmRoles.includes('beheerder') || realmRoles.includes('admin');
}

function tokenBelongsToThisClient(token: string): boolean {
  const payload = decodeJwtPayload<{ azp?: string }>(token);
  const azp = typeof payload?.azp === 'string' ? payload.azp : null;
  const expected = config.oidc.clientId;
  if (!expected) return true;
  return !azp || azp === expected;
}

export default function AuthGuard() {
  const { isAuthenticated, isLoading, user, getAccessTokenSilently, logout }
    = useOidcContext();
  const t = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Guard against the restore effect firing twice within one mount (React 18 StrictMode).
  const restoreFiredRef = useRef(false);
  const [orgInfo, setOrgInfo] = useState<OrgInfo | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isBeheerder, setIsBeheerder] = useState(false);
  const [signupInProgress, setSignupInProgress] = useState(false);
  const [signupOrgName, setSignupOrgName] = useState<string | undefined>(undefined);
  const [fatalError, setFatalError] = useState<string | null>(null);
  // Captured at mount so a transient sessionStorage clear mid-render doesn't flip the recovery card off.
  const [directBrokerIntent] = useState<IdpAlias | null>(() => readDirectBrokerIntent());
  const [orgSignupSubmitting, setOrgSignupSubmitting] = useState(false);
  // Set after /org_join: current token predates the approval row, so /my-org can 403 briefly. Retry once before flipping to accessDenied.
  const joinJustCompletedRef = useRef(false);
  // Dedupe so React 18 StrictMode doesn't double-hit /portal/my-org per mount.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const signupFiredRef = useRef(false);
  // Authenticated probe — anonymous callers must not be able to enumerate claimed email domains.
  const [joinableOrg, setJoinableOrg] = useState<JoinableOrg | null>(null);
  const [joinSubmitting, setJoinSubmitting] = useState(false);

  const completeSignup = useCallback(async (intent: SignupIntent) => {
    setSignupOrgName(intent.organizationName);
    setSignupInProgress(true);
    const startedAt = Date.now();
    const token = await getAccessTokenSilently();
    await api.orgSignup(token, {
      organization_name: intent.organizationName,
      email: intent.email,
    });
    // Hold the "Setting up" card long enough to read — POST + KC logout otherwise flashes by.
    const MIN_VISIBLE_MS = 1400;
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_VISIBLE_MS) {
      await new Promise((r) => setTimeout(r, MIN_VISIBLE_MS - elapsed));
    }
    // Full logout + relogin: KC's existing SSO session predates the org membership and won't pick up the new org claim on `prompt=login`. The `auto` param tells LoginPage to auto-submit so the user lands straight back through the broker with a fresh session.
    // Broker alias carried as URL param — sessionStorage handoff would already be cleared by the time LoginPage reads it.
    const returnTo
      = `${window.location.origin}/login?auto=${encodeURIComponent(intent.email)}`
        + `&idp=${encodeURIComponent(intent.idpAlias)}`;
    await logout({ logoutParams: { returnTo } });
  }, [getAccessTokenSilently, logout]);

  const loadOrgInfo = useCallback(() => {
    if (!isAuthenticated) return;
    if (inFlightRef.current) return;
    setOrgLoading(true);
    const promise = getAccessTokenSilently()
      .then(async (token) => {
        if (!tokenBelongsToThisClient(token)) {
          // Wrong-client token (e.g. leftover dashboard session) — wipe and force a fresh portal login.
          localStorage.removeItem('portal_organization');
          await logout({
            logoutParams: { returnTo: window.location.origin + '/login' },
          });
          return;
        }
        setIsBeheerder(isBeheerderToken(token));
        const info = await api.getMyOrg(token);
        setOrgInfo(info);
        setAccessDenied(false);
        joinJustCompletedRef.current = false;
      })
      .catch(async (err: Error & { status?: number, name?: string }) => {
        if (err?.name === 'SessionExpired' || err?.status === 401) {
          // KC session gone — force a clean logout to reset cookies, then signal expiry to LoginPage.
          localStorage.removeItem('portal_organization');
          try {
            await logout({
              logoutParams: {
                returnTo: window.location.origin + '/login?expired=1',
              },
            });
          } catch {
            window.location.href = window.location.origin + '/login?expired=1';
          }
          return;
        }
        if (err.status === 403 && joinJustCompletedRef.current) {
          // Read-your-write race after /org_join — retry once before showing accessDenied.
          joinJustCompletedRef.current = false;
          await new Promise((r) => setTimeout(r, 800));
          try {
            const token = await getAccessTokenSilently();
            const info = await api.getMyOrg(token);
            setOrgInfo(info);
            setAccessDenied(false);
            return;
          } catch (retry: unknown) {
            const retryErr = retry as Error & { status?: number };
            if (retryErr.status === 403) {
              setAccessDenied(true);
              return;
            }
          }
        }
        if (err.status === 403) {
          setAccessDenied(true);
        }
      })
      .finally(() => {
        setOrgLoading(false);
        inFlightRef.current = null;
      });
    inFlightRef.current = promise;
  }, [isAuthenticated, getAccessTokenSilently]);

  const completeJoin = useCallback(async (email: string) => {
    setJoinSubmitting(true);
    const token = await getAccessTokenSilently();
    await api.orgJoin(token, email);
    // Flag the read-your-write window so loadOrgInfo retries once on 403.
    joinJustCompletedRef.current = true;
    setJoinableOrg(null);
    setJoinSubmitting(false);
    // Pending join doesn't grant the org claim; re-fetching /my-org surfaces `join_status='pending'` so we render MemberPendingPage.
    loadOrgInfo();
  }, [getAccessTokenSilently, loadOrgInfo]);

  useEffect(() => {
    if (!accessDenied) {
      setJoinableOrg(null);
      return;
    }
    let cancelled = false;
    getAccessTokenSilently()
      .then(async (token) => {
        const res = await api.getJoinableOrg(token);
        if (cancelled || !res) return;
        setJoinableOrg({
          idp_org_id: res.idp_org_id,
          organization_name: res.organization_name,
        });
      })
      .catch(() => {
        // Probe failure leaves the AccessDenied card visible; user can retry via "Sign out and try again".
      });
    return () => {
      cancelled = true;
    };
  }, [accessDenied, getAccessTokenSilently]);

  // Restore the URL captured before /login once the user is fully authenticated
  // and the org check has cleared. Held back until orgInfo is loaded so we don't
  // bounce a user who will end up on the access-denied / pending screens.
  useEffect(() => {
    if (restoreFiredRef.current) return;
    if (!isAuthenticated || isLoading || orgLoading) return;
    if (!orgInfo || accessDenied) return;
    const stash = sessionStorage.getItem(RETURN_TO_KEY);
    if (!isSafeReturnTo(stash)) return;
    if (stash === location.pathname + location.search) {
      sessionStorage.removeItem(RETURN_TO_KEY);
      return;
    }
    restoreFiredRef.current = true;
    sessionStorage.removeItem(RETURN_TO_KEY);
    navigate(stash, { replace: true });
  }, [isAuthenticated, isLoading, orgLoading, orgInfo, accessDenied, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated || signupFiredRef.current) return;
    // Email-guard: stale intent from a previous user must not trigger signup for the current user.
    const intent = readSignupIntent(user?.email);
    if (intent) {
      clearSignupIntent();
      signupFiredRef.current = true;
      completeSignup(intent).catch(() => {
        // Partial KC state may remain — surface a loud error rather than a silent fallback.
        setSignupInProgress(false);
        setFatalError(t('errors.signupFailed'));
      });
      return;
    }
    loadOrgInfo();
  }, [isAuthenticated, user?.email, completeSignup, loadOrgInfo, t]);

  if (fatalError) {
    return (
      <FatalErrorCard
        message={fatalError}
        onLogout={() =>
          logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
        }
      />
    );
  }

  if (signupInProgress) {
    return <SignupProgress stage="creating" organizationName={signupOrgName} />;
  }

  if (isLoading || orgLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="background.default">
        <CircularProgress size={32} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Stash the original URL so the OIDC round-trip (which always lands back on
    // `${origin}/`) can be restored after authentication completes — see the
    // restore effect below.
    const current = location.pathname + location.search;
    if (current !== '/' && current !== '/login' && isSafeReturnTo(current)) {
      sessionStorage.setItem(RETURN_TO_KEY, current);
    }
    return <Navigate to="/login" replace />;
  }

  if (orgInfo?.join_status === 'pending' || orgInfo?.join_status === 'rejected') {
    return (
      <MemberPendingPage
        status={orgInfo.join_status}
        organizationName={orgInfo.organization_name}
      />
    );
  }

  if (accessDenied && joinableOrg) {
    return (
      <JoinPromptCard
        organizationName={joinableOrg.organization_name}
        submitting={joinSubmitting}
        onJoin={() => {
          // Resolve email from the access token (not UI state) for auditable logging.
          getAccessTokenSilently()
            .then((token) => {
              const payload = decodeJwtPayload<{ email?: string }>(token);
              return completeJoin(payload?.email ?? '');
            })
            .catch(() => {
              setFatalError(t('errors.joinFailed'));
            });
        }}
        onCancel={() => {
          localStorage.removeItem('portal_organization');
          void logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
        }}
      />
    );
  }

  if (accessDenied && directBrokerIntent && user?.email) {
    // Direct-broker login with no org (and none joinable) → treat as fresh signup; completeSignup handles the relogin bounce that adds the org claim.
    return (
      <OrgSignupPromptCard
        email={user.email}
        submitting={orgSignupSubmitting}
        onSubmit={(organizationName) => {
          sessionStorage.removeItem(DIRECT_BROKER_INTENT_KEY);
          setOrgSignupSubmitting(true);
          completeSignup({
            email: user.email!,
            organizationName,
            idpAlias: directBrokerIntent,
          }).catch(() => {
            setOrgSignupSubmitting(false);
            setFatalError(t('errors.signupFailed'));
          });
        }}
        onCancel={() => {
          sessionStorage.removeItem(DIRECT_BROKER_INTENT_KEY);
          localStorage.removeItem('portal_organization');
          void logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
        }}
      />
    );
  }

  if (accessDenied) {
    return (
      <AccessDeniedCard
        onRetry={() => {
          localStorage.removeItem('portal_organization');
          void logout({ logoutParams: { returnTo: window.location.origin + '/login' } });
        }}
      />
    );
  }

  // First-time beheerder: no platform requested yet
  if (isBeheerder && orgInfo?.hub_tier === 'none' && !orgInfo?.pending_platform_request) {
    return <RegistrationPage onSubmitted={loadOrgInfo} />;
  }

  // Beheerder waiting for platform request approval
  if (isBeheerder && orgInfo?.hub_tier === 'none' && orgInfo?.pending_platform_request) {
    return <PendingPage />;
  }

  return (
    <OrgInfoContext.Provider value={{ orgInfo, isBeheerder, refreshOrgInfo: loadOrgInfo }}>
      <Outlet />
    </OrgInfoContext.Provider>
  );
}
